import {
    createContext,
    useState,
    useEffect,
    useContext,
    useCallback,
    useRef,
} from "react";


import { acceptInviteAPICall, changePasswordAPICall, confirmForgotPasswordAPICall, createAccountAPICall, createAccountWithProjectAPICall, currentUserAPICall, enable2FAAPICall, getMicrosoftSSOLoginLinkAPICall, getGoogleSSOLoginLinkAPICall, getAppleSSOLoginLinkAPICall, getOTPQrcodeAPICall, logoutAPICall, removeUserAPICAll, resetPasswordAPICall, signInAPICall, updateUserAPICall } from "../api/auth/userService";
import LocalStorage from "../helper/LocalStorage";

export const UserContext = createContext({
    user: [],
    userRef: [],
    currentUser: () => { },
    handleSetUser: () => { },
    createUser: () => { },
    createUserWithProject: () => { },
    loginUser: () => { },
    logoutUser: () => { },
    removeAccount: () => { },
    resetPassword: () => { },
    changePassword: () => { },
    confirmForgotPasswor: () => { },
    fetchCurrentUser: () => { },
    updateUser: () => { },
    navigateToDashboardIfPossible: () => { },
    accpetInvitation: () => { },
    enable2FA: () => { },
    getOTPQrcode: () => { },
    getMicrosoftSSOLoginLink: () => { },
    getGoogleSSOLoginLink: () => { },
    getAppleSSOLoginLink: () => { },
});

const UserContextProvider = ({ children, setLoading }) => {
    const [user, setUser] = useState(undefined);

    const userRef = useRef(null);

    const currentUser = () => {
        return LocalStorage.getCurrentUser();
    };

    const handleSetUser = (user) => {
        setUser(user);
        LocalStorage.setCurrentUser(user);
    };

    const loginUser = useCallback(async (email, password, otpCode, onSuccess, onError) => {
        signInAPICall(
            email,
            password,
            otpCode,
            function (response) {
                handleLoggedInUserResponse(response);
                onSuccess(response);
            },
            onError
        );
    });

    const logoutUser = useCallback(async (onSuccess, onError) => {
        logoutAPICall(
            function (response) {
                LocalStorage.logoutUser();
                onSuccess(response);
            },
            function (error) {
                LocalStorage.logoutUser();
                onError(error);
            }
        );
    });

    const removeAccount = useCallback(async (onSuccess, onError) => {
        removeUserAPICAll(
            function (response) {
                LocalStorage.logoutUser();
                onSuccess(response);
            },
            onError
        );
    });

    const fetchCurrentUser = useCallback(async (onSuccess, onError) => {
        currentUserAPICall(function (response) {
            handleUserResponse(response);
            onSuccess(response);
        }, onError);
    });

    const createUser = useCallback(
        async (email, password, name, onSuccess, onError) => {
            createAccountAPICall(
                email,
                password,
                name,
                function (response) {
                    handleLoggedInUserResponse(response);
                    onSuccess(response);
                },
                onError
            );
        }
    );
    const createUserWithProject = useCallback(
        async (email, password, name, projectName, plan, currency, onSuccess, onError) => {
            createAccountWithProjectAPICall(
                email,
                password,
                name,
                projectName,
                plan,
                currency,
                function (response) {
                    handleLoggedInUserResponse(response);
                    onSuccess(response);
                },
                onError
            );
        }
    );
    const updateUser = useCallback(
        async (data, onSuccess, onError) => {
            updateUserAPICall(
                data,
                function (response) {
                    handleUserResponse(response);
                    onSuccess(response);
                },
                onError
            );
        }
    );

    const accpetInvitation = useCallback(
        async (inviteToken, name, password, onSuccess, onError) => {
            acceptInviteAPICall(
                inviteToken, name, password,
                function (response) {
                    handleLoggedInUserResponse(response);
                    onSuccess(response);
                },
                onError
            );
        }
    );

    const resetPassword = useCallback(async (email, onSuccess, onError) => {
        resetPasswordAPICall(email, onSuccess, onError);
    });

    const changePassword = useCallback(
        async (oldPassword, newPassword, otpCode, onSuccess, onError) => {
            changePasswordAPICall(oldPassword, newPassword, otpCode, onSuccess, onError);
        }
    );

    const confirmForgotPassword = useCallback(
        async (token, password, onSuccess, onError) => {
            confirmForgotPasswordAPICall(token, password, onSuccess, onError);
        }
    );

    const enable2FA = useCallback(
        async (enabled, otpCode, onSuccess, onError) => {
            enable2FAAPICall(
                enabled, otpCode,
                function (response) {
                    onSuccess(response);
                },
                onError
            );
        }
    );

    const getOTPQrcode = useCallback(
        async (onSuccess, onError) => {
            getOTPQrcodeAPICall(
                function (response) {
                    onSuccess(response);
                },
                onError
            );
        }
    );

    const getMicrosoftSSOLoginLink = useCallback(
        async (onSuccess, onError) => {
            getMicrosoftSSOLoginLinkAPICall(
                function (response) {
                    onSuccess(response);
                },
                onError
            );
        }
    );

    const getGoogleSSOLoginLink = useCallback(
        async (onSuccess, onError) => {
            getGoogleSSOLoginLinkAPICall(
                function (response) {
                    onSuccess(response);
                },
                onError
            );
        }
    );

    const getAppleSSOLoginLink = useCallback(
        async (onSuccess, onError) => {
            getAppleSSOLoginLinkAPICall(
                function (response) {
                    onSuccess(response);
                },
                onError
            );
        }
    );

    const handleLoggedInUserResponse = (response) => {
        handleUserResponse(response);
        LocalStorage.setAuthenthicationToken(response.data.token);
        LocalStorage.setRefreshToken(response.data.refresh_token);
        LocalStorage.removeBridgeOptedOut();
    };

    const handleUserResponse = (response) => {
        userRef.current = response.data.user;
        setUser(userRef.current)
        LocalStorage.setCurrentUser(response.data.user);
    };

    return (
        <UserContext.Provider
            value={{
                user,
                userRef,
                createUser,
                createUserWithProject,
                loginUser,
                logoutUser,
                removeAccount,
                resetPassword,
                changePassword,
                confirmForgotPassword,
                fetchCurrentUser,
                updateUser,
                currentUser,
                handleSetUser,
                accpetInvitation,
                enable2FA,
                getOTPQrcode,
                getMicrosoftSSOLoginLink,
                getGoogleSSOLoginLink,
                getAppleSSOLoginLink,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};

export const useUserContext = () => useContext(UserContext);

export default UserContextProvider;
